import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useRecordingStore } from '@/stores/recording'
import { onWsEvent, useWsStore } from '@/stores/websocket'
import type { Note } from '@/api/types'

/**
 * What the pop-out scribe is doing right now, as one value.
 *
 * The recording store is the source of truth for capture, but it deliberately
 * CLEARS `noteId`/`noteData` the moment an upload lands — the detail pane takes
 * over from there. The pop-out has no detail pane to hand off to, so it
 * remembers the note it was working on and follows it to completion itself.
 * That memory is the only state this hook owns; everything else is read
 * straight from the store, so `stores/recording.ts` is untouched.
 */
export type ScribePhase =
  | 'idle'
  | 'recording'
  | 'paused'
  | 'processing'
  | 'ready'
  | 'failed'

export interface ScribeSession {
  phase: ScribePhase
  elapsed: number
  note: Note | null
  /** Plain text, exactly what lands on the clipboard. Empty until ready. */
  noteText: string
  /**
   * The capture died mid-consult and a recovery path salvaged what had already
   * reached the server, so the note covers only part of the encounter. The
   * schema requires this to be surfaced wherever a clinician reads the note
   * before relying on it — and this window is the surface they copy into the
   * EMR from, which makes it the last place it can still be caught.
   */
  captureInterruptedAt: string | null
  /** Forget the finished note and collapse back to idle. */
  dismiss: () => void
}

/**
 * `['note', id]` on purpose — the same key `useNote` uses, so the pop-out and
 * the main tab read one cache rather than two copies that can disagree. That
 * is what makes "the note stays in both places" (Option A) true rather than
 * merely rendered twice.
 */
function useFollowedNote(noteId: number | null, active: boolean) {
  const qc = useQueryClient()
  const wsStatus = useWsStore((s) => s.status)

  // WebSocket is the mechanism, per the house rule against polling for
  // real-time updates: the backend broadcasts `note_generated` /
  // `note_changed`, so the window learns the note is ready the moment it is,
  // with no latency.
  //
  // `ws.reconnected` matters as much as the note events. The socket store
  // dispatches it after an outage precisely so consumers can refetch what the
  // drop swallowed — and a `note_generated` lost during a reconnect is exactly
  // the event whose absence strands this window on a spinner.
  useEffect(() => {
    if (noteId === null || !active) return
    return onWsEvent((event) => {
      const isThisNote =
        (event.type === 'note_generated' || event.type === 'note_changed') &&
        event.note_id === noteId
      if (isThisNote || event.type === 'ws.reconnected') {
        void qc.invalidateQueries({ queryKey: ['note', noteId] })
      }
    })
  }, [noteId, active, qc])

  return useQuery({
    queryKey: ['note', noteId],
    queryFn: () => api.get<Note>(`/notes/${noteId}`),
    // Gated on the window being open: this hook lives at the app root, and
    // following a note for every user whether or not they use the pop-out
    // buys nothing.
    enabled: noteId !== null && active,
    // Polls ONLY while the socket is down — which is the case the house rule
    // actually permits, since with no socket there is no server-side change
    // trigger to rely on. With a healthy socket this is zero requests.
    //
    // It stays bounded either way: it stops the moment the note settles. The
    // failure it exists for is specific — a note that finishes generating
    // while the socket is down leaves this window on a spinner forever, and
    // unlike the notes list there is no other surface to notice on. That reads
    // as a lost consult.
    refetchInterval: (query) => {
      const note = query.state.data as Note | undefined
      if (note && isSettled(note)) return false
      return wsStatus === 'connected' ? false : 15_000
    },
    refetchOnWindowFocus: true,
  })
}

/** Generation is finished — successfully or not — and the text will not change. */
function isSettled(note: Note): boolean {
  if (note.error) return true
  if (note.processing_stage !== null) return false
  return note.status !== 'processing'
}

/**
 * The note the pop-out was opened ON, when it was opened from a note rather
 * than from a live recording. `key` changes on every open so that reopening
 * the window on a note already dismissed shows it again.
 */
export interface ScribeSeed {
  noteId: number | null
  key: number
}

/**
 * @param active whether the pop-out is open. The note latch runs either way —
 *   so opening the window mid-generation still knows which note to show — but
 *   the network follow only runs while something is actually watching.
 * @param seed the note the window was opened on, if any.
 */
export function useScribeSession(active = true, seed?: ScribeSeed): ScribeSession {
  const state = useRecordingStore((s) => s.state)
  const elapsed = useRecordingStore((s) => s.elapsed)
  const storeNoteId = useRecordingStore((s) => s.noteId)
  const storeNoteData = useRecordingStore((s) => s.noteData)
  const generateResult = useRecordingStore((s) => s.generateResult)

  // `latched` is what we are showing. The two `seen*` fields record what has
  // already been consumed from each source, so that dismissing does not
  // immediately re-latch the same thing while the store (or the seed) still
  // names it.
  //
  // They are tracked SEPARATELY on purpose. With one shared key, finishing a
  // recording cleared the store, the seed then looked "new" again, and the
  // window fell back to the note it had been opened on — throwing away the
  // note the clinician had just recorded. The seed is an opening fallback, not
  // a standing one.
  const [{ latched, seenStoreKey, seenSeedKey }, setLatch] = useState<{
    latched: number | null
    seenStoreKey: string | null
    seenSeedKey: number | null
  }>({ latched: null, seenStoreKey: null, seenSeedKey: null })

  // Two sources, and the ORDER is the rule: a note being recorded always beats
  // the note the window happened to be opened on. Otherwise starting a consult
  // from a patient's old note would leave the window showing the old one.
  //
  // All three store fields are cleared at different moments during an upload,
  // so reading them directly at render time gives a window that blanks out the
  // instant the upload succeeds — hence the latch.
  //
  // Adjusted during render rather than in an effect: React re-runs this
  // component before committing, so there is no cascading render and no
  // intermediate frame showing the wrong phase. An effect here would both trip
  // `react-hooks/set-state-in-effect` and paint one frame of 'idle' between the
  // store clearing and the latch catching up.
  const fromStore = storeNoteId ?? storeNoteData?.id ?? generateResult?.noteId ?? null
  const storeKey = fromStore !== null ? `store:${fromStore}` : null

  if (fromStore !== null && storeKey !== seenStoreKey) {
    setLatch((s) => ({ ...s, latched: fromStore, seenStoreKey: storeKey }))
  } else if (fromStore === null && seed?.noteId != null && seed.key !== seenSeedKey) {
    setLatch((s) => ({ ...s, latched: seed.noteId, seenSeedKey: seed.key }))
  }

  const followedId = latched
  const { data: note } = useFollowedNote(followedId, active)

  const phase = derivePhase(state, followedId, note ?? null)

  return {
    phase,
    elapsed,
    note: note ?? null,
    noteText: phase === 'ready' ? (note?.note ?? '') : '',
    captureInterruptedAt: note?.capture_interrupted_at ?? null,
    // Keeps `seenKey`, so the note just dismissed cannot re-latch from a store
    // field (or seed) that still names it. A new recording, or reopening the
    // window, produces a different key and latches again.
    dismiss: () => setLatch((s) => ({ ...s, latched: null })),
  }
}

function derivePhase(
  state: 'idle' | 'recording' | 'paused' | 'processing',
  followedId: number | null,
  note: Note | null,
): ScribePhase {
  // Capture always wins: a clinician who has started a new consult must see
  // the recorder, not the previous note still sitting there.
  if (state === 'recording') return 'recording'
  if (state === 'paused') return 'paused'
  if (state === 'processing') return 'processing'

  if (followedId === null) return 'idle'
  if (!note) return 'processing'

  // `error` means the body is a backend-written failure message, not clinical
  // content — offering Copy on it would paste an apology into the record.
  if (note.error) return 'failed'
  if (!isSettled(note)) return 'processing'
  if (!note.note || note.note.trim() === '') return 'processing'
  return 'ready'
}
